import {ApiResponse, BodyType, HttpMethod, sendRequest} from "./sendRequest";
import {ApplyReviewPayload, CardDto, CardGenerationStateDto, CardMarkDto, UnmarkResult, LlmProvider, LlmSessionDetailDto, LlmSessionSummaryDto, PracticeSubcardPayload, QuickAddItemDto, ReviewCard, SetMarkupPayload, SetStateResult, SttTokenDto, TtsPayload, TtsVoicesDto} from "../common";
import {dataUrlToBlob} from "./dataUrlToBlob";
import {AuthConfigDto, SessionDto} from "../auth/auth.interface";
import {SetStatePayload} from "../cards/cards.slice";
import {SubcardDetailData, SubcardHeatmapData} from "../cards/subcardStatistics/subcardStatistics.interface";

export const api = {
    applyReview(params: ApplyReviewPayload) {
        const body: ApplyReviewPayload = params;

        return sendRequest<void>('/api/review/apply', HttpMethod.POST, body, BodyType.Json);
    },

    practiceSubcard(params: PracticeSubcardPayload): Promise<void> {
        return sendRequest<void>('/api/review/practice-subcard', HttpMethod.POST, params, BodyType.Json);
    },

    getNextReviewCard(cardId?: string, exclude: string[] = []): Promise<ReviewCard> {
        const params: string[] = [];
        if (cardId !== undefined && cardId !== '')
            params.push(`cardId=${encodeURIComponent(cardId)}`);
        if (exclude.length > 0)
            params.push(`exclude=${exclude.map(encodeURIComponent).join(',')}`);
        const query = params.length > 0 ? `?${params.join('&')}` : '';
        return sendRequest<ReviewCard>(`/api/review/next${query}`);
    },

    sttToken(): Promise<SttTokenDto> {
        return sendRequest<SttTokenDto>('/api/stt/token', HttpMethod.POST, {}, BodyType.Json);
    },

    ttsVoices(): Promise<TtsVoicesDto> {
        return sendRequest<TtsVoicesDto>('/api/tts/voices');
    },

    saveTtsVoices(voices: TtsVoicesDto): Promise<TtsVoicesDto> {
        return sendRequest<TtsVoicesDto>('/api/tts/voices', HttpMethod.PUT, voices, BodyType.Json);
    },

    async tts(payload: TtsPayload): Promise<Blob> {
        const response = await fetch('/api/tts', {
            method: HttpMethod.POST,
            body: JSON.stringify(payload),
            headers: {'Content-Type': 'application/json'},
            credentials: 'same-origin',
        });
        if (!response.ok)
            throw new Error(`TTS failed with ${response.status}`);
        return response.blob();
    },

    loadCard(cardId: string): Promise<CardDto> {
        return sendRequest<CardDto>(`/api/card/${encodeURIComponent(cardId)}`);
    },

    setMarkup(params: SetMarkupPayload): Promise<void> {
        return sendRequest<void>(`/api/card/setMarkup`, HttpMethod.POST, params, BodyType.Json);
    },

    setState(params: SetStatePayload): Promise<SetStateResult> {
        return sendRequest<SetStateResult>(`/api/card/setState`, HttpMethod.POST, params, BodyType.Json);
    },

    resetNextCheck(params: {cardId: string}): Promise<void> {
        return sendRequest<void>(`/api/card/resetNextCheck`, HttpMethod.POST, params, BodyType.Json);
    },

    deleteCard(payload: {cardId: string}): Promise<void> {
        return sendRequest<void>(`/api/card/${payload.cardId}`, HttpMethod.DELETE);
    },

    regenerateCard(params: {cardId: string, text?: string, instructions?: string, provider?: LlmProvider}): Promise<CardGenerationStateDto> {
        const {cardId, text, instructions, provider} = params;
        const body: {text?: string, instructions?: string, provider?: LlmProvider} = {};
        if (text !== undefined) body.text = text;
        if (instructions !== undefined) body.instructions = instructions;
        if (provider !== undefined) body.provider = provider;
        return sendRequest<CardGenerationStateDto>(`/api/card/${encodeURIComponent(cardId)}/regenerate`, HttpMethod.POST, body, BodyType.Json);
    },

    invertCard(params: {cardId: string}): Promise<CardGenerationStateDto> {
        return sendRequest<CardGenerationStateDto>(`/api/card/${encodeURIComponent(params.cardId)}/invert`, HttpMethod.POST, {}, BodyType.Json);
    },

    cardGeneration(cardId: string): Promise<CardGenerationStateDto> {
        return sendRequest<CardGenerationStateDto>(`/api/card/${encodeURIComponent(cardId)}/generation`);
    },

    dismissCardGeneration(cardId: string): Promise<void> {
        return sendRequest<void>(`/api/card/${encodeURIComponent(cardId)}/generation/dismiss`, HttpMethod.POST, {}, BodyType.Json);
    },

    markCard(params: {cardId: string, note: string}): Promise<CardMarkDto> {
        return sendRequest<CardMarkDto>(`/api/card/${encodeURIComponent(params.cardId)}/mark`, HttpMethod.PUT, {note: params.note}, BodyType.Json);
    },

    unmarkWholeCard(cardId: string): Promise<UnmarkResult> {
        return sendRequest<UnmarkResult>(`/api/card/${encodeURIComponent(cardId)}/mark`, HttpMethod.DELETE);
    },

    markSubcard(params: {cardId: string, subcardKey: string, note: string}): Promise<CardMarkDto> {
        const {cardId, subcardKey, note} = params;
        return sendRequest<CardMarkDto>(`/api/card/${encodeURIComponent(cardId)}/mark/subcards/${encodeURIComponent(subcardKey)}`, HttpMethod.PUT, {note}, BodyType.Json);
    },

    unmarkSubcard(params: {cardId: string, subcardKey: string}): Promise<UnmarkResult> {
        const {cardId, subcardKey} = params;
        return sendRequest<UnmarkResult>(`/api/card/${encodeURIComponent(cardId)}/mark/subcards/${encodeURIComponent(subcardKey)}`, HttpMethod.DELETE);
    },

    unmarkCards(cardIds: string[]): Promise<void> {
        return sendRequest<void>('/api/card/unmark', HttpMethod.POST, {cardIds}, BodyType.Json);
    },

    revertCard(params: {cardId: string}): Promise<void> {
        return sendRequest<void>(`/api/card/${encodeURIComponent(params.cardId)}/revert`, HttpMethod.POST, {}, BodyType.Json);
    },

    addCard(params: {
        markup: string,
        images: Record<number, string>,
    }): Promise<void> {
        const body = new FormData();
        body.append('markup', params.markup);
        if (Object.keys(params.images).length > 0) {
            for (const key in params.images) {
                const blob = dataUrlToBlob(Object.values(params.images)[key]);

                body.append('image', blob, key);
            }
        }

        return sendRequest<void>('/api/card', HttpMethod.PUT, body, BodyType.FormData);
    },

    authConfig(): Promise<AuthConfigDto> {
        return sendRequest<AuthConfigDto>('/api/auth/config');
    },

    session(): Promise<SessionDto> {
        return sendRequest<SessionDto>('/api/auth/session');
    },

    googleLogin(credential: string): Promise<SessionDto> {
        return sendRequest<SessionDto>('/api/auth/google', HttpMethod.POST, {credential}, BodyType.Json);
    },

    logout(): Promise<void> {
        return sendRequest<void>('/api/auth/logout', HttpMethod.POST);
    },

    search(params: { text?: string, tag?: string, state?: number, marked?: boolean }) {
        const searchText = params.text !== undefined && params.text !== null ? params.text : '';
        let query = 'text=' + encodeURIComponent(searchText);
        if (params.tag !== undefined && params.tag !== null && params.tag !== '') {
            query += '&tag=' + encodeURIComponent(params.tag);
        }
        if (params.state !== undefined) {
            query += '&state=' + params.state;
        }
        if (params.marked === true) {
            query += '&marked=1';
        }
        return sendRequest<ApiResponse>('/api/search?' + query, HttpMethod.GET);
    },

    quickAdd: {
        create(text: string): Promise<QuickAddItemDto> {
            return sendRequest<QuickAddItemDto>('/api/quickAdd', HttpMethod.POST, {text}, BodyType.Json);
        },
        list(): Promise<QuickAddItemDto[]> {
            return sendRequest<QuickAddItemDto[]>('/api/quickAdd');
        },
        process(id: string): Promise<QuickAddItemDto> {
            return sendRequest<QuickAddItemDto>(`/api/quickAdd/${encodeURIComponent(id)}/process`, HttpMethod.POST, {}, BodyType.Json);
        },
        remove(id: string): Promise<void> {
            return sendRequest<void>(`/api/quickAdd/${encodeURIComponent(id)}`, HttpMethod.DELETE);
        },
        clearFinished(): Promise<void> {
            return sendRequest<void>('/api/quickAdd', HttpMethod.DELETE);
        },
    },

    statistics: {
        getSubcardHeatmap(cardId: string): Promise<SubcardHeatmapData> {
            return sendRequest<SubcardHeatmapData>(`/api/statistics/subcard-heatmap/${encodeURIComponent(cardId)}`);
        },
        getSubcardDetail(cardId: string): Promise<SubcardDetailData> {
            return sendRequest<SubcardDetailData>(`/api/statistics/subcard-detail/${encodeURIComponent(cardId)}`);
        }
    },

    llmLog: {
        getSessions(): Promise<LlmSessionSummaryDto[]> {
            return sendRequest<LlmSessionSummaryDto[]>('/api/llm-log/sessions');
        },
        getSession(id: string): Promise<LlmSessionDetailDto> {
            return sendRequest<LlmSessionDetailDto>(`/api/llm-log/sessions/${encodeURIComponent(id)}`);
        },
    },
}
